/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import {
    clearSystemHealthStatsInterval,
    logSystemHealthStats,
} from '../../store/healthstats/systemhealthstats.reducers';
import { RootState } from '../../store/root.reducers';
import { appStart } from '../../store/appl/appl.reducers';
import {
    KeyboardEvents,
    URLChangeEvents,
    VisibilityEvents,
    NetworkChangeEvents,
    MouseEvents,
    CursorEvents,
    SystemInfoEvent,
} from '../Events';
import { Guide } from '../Guide';
import { SplashScreen } from '../SplashScreen';
import { DeviceModel } from '../../core/device';
import { Log } from '../../core/logging/Log';
import { SignInScreen } from '../SignInScreen';
import { ErrorScreen } from '../ErrorScreen';
import { SettingsScreen } from '../SettingsScreen';
import { setIsApplicationReadyAction } from '../../store/appl/appl.actions';
import { setLocationUrl } from '../../store/urlchangeevents/urlchangeevents.reducers';
import { Locale } from '../../core/locale/Locale';
import {
    DEFAULT_LOCALE,
    KAVERI_GLOBAL_CATCH_NAME,
    KAVERI_GLOBAL_CATCH_RETRY_LIMIT,
} from '../../config';
import { useGlobalCatch } from '../hooks';
import { sos } from '../../store/error/erroroverlays.reducers';
import { OverlayDialog } from '../OverlayDialog';
import { FullScreenPlayback } from '../FullScreenPlayback';
import { VideoPlayer } from '../VideoPlayer';
import { CcbSwitch, CcbSwitchImpl } from '../../core/utils/ccb';
import { CcbSwitchName } from '../../core/utils/ccb/enums';

import './App.scss';

const App: FunctionComponent = (): JSX.Element => {
    const TAG = 'App';

    const dispatch = useDispatch();

    const { isApplicationReady } = useSelector((state: RootState) => state.appl);

    const globalCatches = useGlobalCatch({
        name: KAVERI_GLOBAL_CATCH_NAME,
        onCatch: DeviceModel.restartApplication,
        retryLimit: KAVERI_GLOBAL_CATCH_RETRY_LIMIT,
    });

    const initCcbSwitch = () => {
        const oCcbSwitchImpl = new CcbSwitchImpl();
        oCcbSwitchImpl
            .initialize()
            .then(() => {
                CcbSwitch.setCcbSwitchImpl(oCcbSwitchImpl);
                if (CcbSwitch.getInstance().isEnabled(CcbSwitchName.ENABLE_DEVELOPER_SANDBOX)) {
                    Log.setLogLevel(Log.LogLevel.INFO);
                }
            })
            .catch((error) => {
                Log.e(TAG, 'Failed to initalize CcbSwitch', error);
            });
    };

    useEffect(() => {
        initCcbSwitch();
        Locale.setLocale(DEFAULT_LOCALE);
        DeviceModel.initialize()
            .then(() => {
                dispatch(setIsApplicationReadyAction(true));
                Log.i(TAG, 'DeviceModel initialized successfully');
            })
            .catch(() => {
                Log.w(
                    TAG,
                    'Failed to initialize DeviceModel. Starting application with default properties.',
                );
            })
            .finally(() => {
                if (true) {
                    if (globalCatches.isRetriesDone()) {
                        dispatch(sos());
                    } else {
                        dispatch(appStart());
                    }
                } else {
                    window.open('./404.html', '_self');
                }
            });
        return () => {
            dispatch(logSystemHealthStats());
            dispatch(clearSystemHealthStatsInterval());
        };
    }, []);

    return (
        <React.Fragment>
            <NetworkChangeEvents />
            <MouseEvents />
            <CursorEvents />
            <SystemInfoEvent />
            <KeyboardEvents>
                <VideoPlayer />
                <BrowserRouter basename={DeviceModel.getAppHomePage()}>
                    <URLChangeEvents />
                    <VisibilityEvents />
                    <Switch>
                        <Route path="/" exact>
                            <SplashScreen />
                        </Route>
                        <Route path="/error">
                            <ErrorScreen />
                        </Route>
                        <Route path="/guide">
                            {isApplicationReady ? <Guide /> : dispatch(setLocationUrl('/'))}
                        </Route>
                        <Route path="/settings">
                            <SettingsScreen />
                        </Route>
                        <Route path="/signin">
                            <SignInScreen />
                        </Route>
                        {/* TODO: Resolution has to be added for node versions beyond 14.8.3. */}
                        <Route path="/video-player">
                            {isApplicationReady ? (
                                <FullScreenPlayback />
                            ) : (
                                dispatch(setLocationUrl('/'))
                            )}
                        </Route>
                    </Switch>
                </BrowserRouter>
            </KeyboardEvents>
            <OverlayDialog />
        </React.Fragment>
    );
};

export default App;
